import { memo } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Link from 'components/Link';
import {
  transformPriceFilterLabel,
  transformVariation,
} from 'data/dutchie/transformations';
import cleanObject from 'utils/clean-object';
import isFilterCategoryRelated from 'utils/is-filter-category-related';
import parseFacetVisibility from 'utils/parse-facet-visibility';
import styles from './CategoryFilterBreadcrumbs.module.scss';

function CategoryFilterBreadcrumbs({
  category,
  className,
  facets,
  filterParam,
  path,
  promotion,
  searchParam,
  showClearAll = true,
  title,
}) {
  const router = useRouter();
  const { hiddenFilterParams, sanitizedParams, visibleFilterCount } =
    parseFacetVisibility(facets, filterParam);

  const bcCategoryFilter =
    sanitizedParams.find((val) => val.includes('Category')) || '';
  const categoryFilterName =
    decodeURIComponent(bcCategoryFilter).split('^')[2] || '';
  const categoryName = categoryFilterName.toLowerCase().replace('_', '-');

  const hasSelections = visibleFilterCount || promotion || searchParam;

  return (
    <div className="pe-10 w-100">
      <div className="d-flex flex-row justify-content-between mb-2 mb-md-10">
        <h2
          className={classNames(
            'font-sofia fs-8 fw-700 mb-0 text-primary text-uppercase',
            styles.title
          )}
        >
          Filtered By
        </h2>
        {showClearAll ? (
          <Link
            shallow
            href={{
              pathname: path,
              query: cleanObject({
                sort: router.query.sort,
                ...(hiddenFilterParams && {
                  filters: hiddenFilterParams.join(','),
                }),
              }),
            }}
            className={classNames(
              'd-block fs-8 fw-400 position-relative text-primary',
              styles.clear_all
            )}
            data-value="clear-all-filters"
          >
            Clear All
          </Link>
        ) : null}
      </div>
      {hasSelections ? null : (
        <span
          className={classNames(
            'd-inline-block lh-1 me-5 mb-5 p-4',
            styles.no_selections
          )}
        >
          None
        </span>
      )}
      <ul
        className={classNames(
          'border-bottom list-unstyled pb-6 pb-md-10 mb-0 mt-0',
          className
        )}
      >
        {promotion ? (
          <li className="border border-primary d-inline-block lh-1 me-5 mb-5 p-4">
            <Link
              shallow
              href={{
                pathname: path,
                query: cleanObject({
                  sort: router.query.sort,
                  filters: sanitizedParams.join(','),
                  search: router.query.search,
                }),
              }}
              className={classNames('p-0 m-0', styles.breadcrumb_link)}
              title={`Remove ${title} filter`}
              data-value="category-page-filter"
            >
              {title}
            </Link>
          </li>
        ) : null}
        {searchParam ? (
          <li className="border border-primary d-inline-block lh-1 me-5 mb-5 p-4">
            <Link
              shallow
              href={{
                pathname: path,
                query: cleanObject({
                  sort: router.query.sort,
                  filters: sanitizedParams.join(','),
                }),
              }}
              className={classNames('p-0 m-0', styles.breadcrumb_link)}
              title={`Remove ${searchParam} search filter`}
              data-value="category-page-filter"
            >
              {`Search: ${searchParam}`}
            </Link>
          </li>
        ) : null}
        {/* eslint-disable-next-line complexity */}
        {sanitizedParams.map((param) => {
          const [facetName, facetType, facetValue] =
            decodeURIComponent(param).split('^');
          const isCategoryParam = param.includes('Category');
          const isCategoryOrSubCategoryParam = param
            .toLowerCase()
            .includes('category');
          const paramRemoved = isCategoryParam
            ? sanitizedParams
                .filter((val) => !isFilterCategoryRelated(val))
                .join(',')
            : sanitizedParams.filter((val) => val !== param).join(',');

          const currentFacet = facets.find(
            (value) => value.facet === facetName
          );

          if (currentFacet && !currentFacet.visible) return null;

          let name;

          if (facetType === 'range' && facetValue !== 'Closeout') {
            name = param;
            const paramValues = facetValue
              .split(' ')
              .map((val) => Number.parseInt(val, 10))
              .filter(Boolean);
            const [from, to] = paramValues;
            name = `$${from / 100}${to ? `-$${to / 100}` : '+'}`;
          } else if (facetName === 'Price') {
            let customName;
            if (facetValue.includes('CUSTOM_PRICE_RANGE_')) {
              const splitValue = facetValue.split('_');
              customName =
                splitValue.length === 5
                  ? `$${splitValue[3]} to $${splitValue[4]}`
                  : undefined;
            }
            name = customName ?? transformPriceFilterLabel(facetValue);
          } else if (
            isCategoryOrSubCategoryParam ||
            facetName === 'Special' ||
            facetName === 'Discount_Type' ||
            facetName === 'Brand' ||
            facetName === 'Tier' ||
            facetName === 'Offers'
          ) {
            if (!currentFacet) {
              return null;
            }

            const currentTerm = currentFacet.value.terms.find(
              ({ id, term }) => term === facetValue || id === facetValue
            );

            name = currentTerm?.displayTerm || currentTerm?.term || facetValue;
          } else {
            name = facetValue === 'Gold' ? 'Gold Label' : facetValue;
          }
          return (
            <li
              key={param}
              className="border border-primary d-inline-block lh-1 me-5 mb-5 p-4"
            >
              <Link
                shallow
                href={{
                  pathname: path,
                  query: cleanObject({
                    ...router.query,
                    sort: router.query.sort,
                    promotion,
                    filters: paramRemoved,
                    search: router.query.search,
                  }),
                }}
                className={classNames('p-0 m-0', styles.breadcrumb_link)}
                title={`Remove ${name} filter`}
                data-value="category-page-filter"
              >
                {transformVariation(name, categoryName || category)}
              </Link>
            </li>
          );
        })}
        {/* eslint-enable complexity */}
      </ul>
    </div>
  );
}

export default memo(CategoryFilterBreadcrumbs);
